import { useState, useEffect } from "react";
import axios from "axios";
import {
  Package, Plus, Clock, Building2, CheckCircle, XCircle, AlertTriangle, Truck, Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNITES = ["kg", "m", "m²", "m³", "pièces", "sacs", "palettes"];

function SkeletonRow() {
  return (
    <div className="skeleton-card mb-3">
      <div className="flex items-center gap-4">
        <div className="skeleton w-10 h-10 rounded-xl" />
        <div className="flex-1">
          <div className="skeleton h-4 w-1/2 mb-2" />
          <div className="skeleton h-3 w-1/3" />
        </div>
        <div className="skeleton h-8 w-24 rounded-lg" />
      </div>
    </div>
  );
}

export default function DemandesMateriaux() {
  const [demandes, setDemandes] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [demandesDemain, setDemandesDemain] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [formData, setFormData] = useState({
    chantier_id: "", date_besoin: "", materiau_nom: "",
    quantite: 1, unite: "pièces", urgence: "normale", notes: ""
  });
  const { t } = useLanguage();
  const { user, isPatron } = useAuth();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [demandesRes, chantiersRes] = await Promise.all([
        axios.get(`${API}/demandes-materiaux`),
        axios.get(`${API}/chantiers`),
      ]);
      setDemandes(demandesRes.data);
      setChantiers(chantiersRes.data);

      if (isPatron()) {
        const demainRes = await axios.get(`${API}/demandes-materiaux/demain`).catch(() => ({ data: [] }));
        setDemandesDemain(demainRes.data);
      }
    } catch (error) {
      console.error(error);
      toast.error(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.chantier_id) { toast.error(t('errorGeneric')); return; }
    try {
      await axios.post(
        `${API}/demandes-materiaux?user_id=${user?.id || ''}&user_nom=${encodeURIComponent((user?.prenom || '') + ' ' + (user?.nom || ''))}`,
        formData
      );
      toast.success(t('requestSent'));
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(t('errorGeneric'));
    }
  };

  const resetForm = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setFormData({
      chantier_id: "", date_besoin: tomorrow.toISOString().split('T')[0],
      materiau_nom: "", quantite: 1, unite: "pièces", urgence: "normale", notes: ""
    });
  };

  const updateStatus = async (id, statut) => {
    try {
      await axios.put(`${API}/demandes-materiaux/${id}`, { statut });
      toast.success(t('success'));
      fetchData();
    } catch (error) {
      toast.error(t('errorGeneric'));
    }
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getStatusBadge = (statut) => {
    const styles = {
      en_attente: "bg-amber-50 text-amber-700 border-amber-200",
      approuve: "bg-emerald-50 text-emerald-700 border-emerald-200",
      refuse: "bg-red-50 text-red-700 border-red-200",
      livre: "bg-blue-50 text-blue-700 border-blue-200"
    };
    const labels = {
      en_attente: t('statusPending'),
      approuve: t('statusApproved'),
      refuse: t('statusRefused'),
      livre: t('statusDelivered')
    };
    return (
      <span className={`px-2 py-0.5 rounded-md text-xs font-medium border ${styles[statut] || styles.en_attente}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  const getChantierName = (id) => chantiers.find(c => c.id === id)?.nom || "?";

  const filteredDemandes = filterStatus === "all" ? demandes : demandes.filter(d => d.statut === filterStatus);

  // Worker gets a simplified view - only sees their chantiers
  const workerChantiers = isPatron() ? chantiers : chantiers.filter(c =>
    c.ouvriers_assignes?.includes(user?.id)
  );

  if (loading) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div><div className="skeleton h-8 w-48 mb-2" /><div className="skeleton h-4 w-36" /></div>
          <div className="skeleton h-10 w-36 rounded-lg" />
        </div>
        {[1,2,3].map(i => <SkeletonRow key={i} />)}
      </div>
    );
  }

  return (
    <div data-testid="demandes-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1E293B] uppercase tracking-tight font-['Barlow_Condensed']">{t('demandesTitle')}</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">{t('demandesSubtitle')}</p>
        </div>
        <Button
          data-testid="btn-new-demande"
          onClick={openNewDialog}
          className="bg-[#F97316] hover:bg-orange-600 text-white rounded-lg uppercase tracking-wide text-sm font-medium shadow-md shadow-orange-200 btn-scale"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('newRequest')}
        </Button>
      </div>

      {/* Materials for Tomorrow - Patrons only */}
      {isPatron() && demandesDemain.length > 0 && (
        <Card className="bg-white border-[#E2E8F0] border-l-4 border-l-[#F97316] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-[#1E293B] flex items-center gap-2">
              <div className="p-2 rounded-lg bg-orange-50">
                <Truck className="w-5 h-5 text-[#F97316]" />
              </div>
              {t('tomorrowTitle')}
              <span className="ml-auto bg-[#F97316] text-white text-xs px-2 py-0.5 rounded-full">
                {demandesDemain.reduce((a, d) => a + (d.materiaux?.length || 0), 0)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {demandesDemain.map((item, idx) => (
                <div key={idx} className="border border-[#E2E8F0] rounded-xl p-3 bg-[#F8FAFC]">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-[#3B82F6]" />
                    <span className="font-semibold text-[#1E293B]">{item.chantier?.nom}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {item.materiaux.map((mat) => (
                      <div key={mat.id} className="flex items-center justify-between bg-white p-2 rounded-lg border border-[#E2E8F0]">
                        <div>
                          <span className="text-[#1E293B] text-sm font-medium">{mat.materiau_nom}</span>
                          <span className="text-[#94A3B8] text-xs ml-2">{mat.quantite} {mat.unite}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {getStatusBadge(mat.statut)}
                          {mat.statut === 'en_attente' && (
                            <div className="flex gap-1 ml-1">
                              <button onClick={() => updateStatus(mat.id, 'approuve')} className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded font-bold hover:bg-emerald-200 transition-colors flex items-center justify-center text-sm">✓</button>
                              <button onClick={() => updateStatus(mat.id, 'refuse')} className="w-6 h-6 bg-red-100 text-red-600 rounded font-bold hover:bg-red-200 transition-colors flex items-center justify-center text-sm">✗</button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter tabs */}
      {isPatron() && demandes.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {['all', 'en_attente', 'approuve', 'refuse', 'livre'].map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterStatus === s ? 'bg-[#1E293B] text-white shadow-sm' : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]'}`}
            >
              {s === 'all' ? 'Tous' : s === 'en_attente' ? t('statusPending') : s === 'approuve' ? t('statusApproved') : s === 'refuse' ? t('statusRefused') : t('statusDelivered')}
              {s !== 'all' && (
                <span className="ml-1.5 text-[10px] opacity-70">
                  ({demandes.filter(d => d.statut === s).length})
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* All Requests */}
      {filteredDemandes.length === 0 ? (
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-[#CBD5E1]" />
            <h3 className="text-xl font-semibold text-[#1E293B] mb-2">{t('noRequest')}</h3>
            <p className="text-[#94A3B8] mb-4">{t('addFirstRequest')}</p>
            <Button onClick={openNewDialog} className="bg-[#F97316] text-white rounded-lg">
              <Plus className="w-4 h-4 mr-2" />{t('newRequest')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredDemandes.map((demande) => (
            <Card key={demande.id} data-testid={`demande-${demande.id}`} className="bg-white border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${demande.urgence === 'urgente' ? 'bg-red-50' : 'bg-blue-50'}`}>
                      {demande.urgence === 'urgente'
                        ? <AlertTriangle className="w-5 h-5 text-red-500" />
                        : <Package className="w-5 h-5 text-[#3B82F6]" />
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#1E293B]">{demande.materiau_nom}</span>
                        <span className="text-[#94A3B8]">×</span>
                        <span className="text-[#1E293B] font-mono font-semibold">{demande.quantite} {demande.unite}</span>
                        {getStatusBadge(demande.statut)}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-[#64748B] mt-1 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-[#94A3B8]" />
                          {getChantierName(demande.chantier_id)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-[#94A3B8]" />
                          {demande.date_besoin}
                        </span>
                        {demande.demande_par_nom && (
                          <span className="text-[#94A3B8] text-xs">{t('by')} {demande.demande_par_nom}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {isPatron() && demande.statut === "en_attente" && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => updateStatus(demande.id, "approuve")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg btn-scale">
                        <CheckCircle className="w-4 h-4 mr-1" />{t('approveBtn')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(demande.id, "refuse")} className="border-red-200 text-red-600 hover:bg-red-50 rounded-lg btn-scale">
                        <XCircle className="w-4 h-4 mr-1" />{t('refuseBtn')}
                      </Button>
                    </div>
                  )}
                  {isPatron() && demande.statut === "approuve" && (
                    <Button size="sm" onClick={() => updateStatus(demande.id, "livre")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg btn-scale">
                      <Truck className="w-4 h-4 mr-1" />{t('markDeliveredBtn')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#1E293B] max-w-md shadow-xl" aria-describedby="demande-form-desc">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#1E293B]">
              {t('newRequest')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t('selectSite')} *</Label>
              <Select value={formData.chantier_id} onValueChange={(v) => setFormData({...formData, chantier_id: v})}>
                <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg h-12">
                  <SelectValue placeholder={t('selectChantier')} />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
                  {workerChantiers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t('dateNeeded')} *</Label>
              <Input
                type="date"
                required
                value={formData.date_besoin}
                onChange={(e) => setFormData({...formData, date_besoin: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg h-12"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t('material')} *</Label>
              <Input
                required
                placeholder="Ex: Ciment, Briques, Câbles..."
                value={formData.materiau_nom}
                onChange={(e) => setFormData({...formData, materiau_nom: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg h-12 text-base"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">{t('quantityField')}</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantite}
                  onChange={(e) => setFormData({...formData, quantite: parseFloat(e.target.value) || 1})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg h-12"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium">{t('unitField')}</Label>
                <Select value={formData.unite} onValueChange={(v) => setFormData({...formData, unite: v})}>
                  <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
                    {UNITES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t('urgency')}</Label>
              <Select value={formData.urgence} onValueChange={(v) => setFormData({...formData, urgence: v})}>
                <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E8F0] text-[#1E293B] shadow-lg">
                  <SelectItem value="normale">{t('normalUrgency')}</SelectItem>
                  <SelectItem value="urgente">{t('urgentUrgency')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#475569] font-medium">{t('notesField')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg min-h-[70px]"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] rounded-lg">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-[#F97316] hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-200 btn-scale">
                {t('submitRequest')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
